import React from 'react';
import cn from 'classnames';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  DropdownButtonSkeleton,
  getFaIcon,
  MenuItemProps,
} from '@odin-labs/components';
import { AuthUser, to } from 'acl';
import { ensureNonEmptyItems, shouldOpenInNewTab } from 'utils';
import { getPrettyFormattedUtcDate } from 'utils/dates';
import {
  faBriefcase,
  BusinessTimeIcon,
  TrashIcon,
  FileChartLineIcon,
  CogIcon,
} from 'components/icons';
import { Jobsite, JobsiteColumn, JobsiteColumnName } from './types';

const BriefcaseIcon = getFaIcon({
  icon: faBriefcase,
  className: cn('odin-text-base odin-text-odin-primary'),
});

export const getColumns = ({
  user,
  isTemplate,
  navigateToJobsiteConfigurationEditPage,
  navigateToJobsitePage,
  openCloseOutJobsiteModal,
  openArchiveJobsiteModal,
}: {
  user: AuthUser;
  isTemplate: boolean;
  navigateToJobsiteConfigurationEditPage: (
    jobsite: Jobsite,
    openInNewTab: boolean,
  ) => void;
  navigateToJobsitePage: (jobsite: Jobsite, openInNewTab: boolean) => void;
  openCloseOutJobsiteModal: (jobsite: Jobsite) => void;
  openArchiveJobsiteModal: (jobsite: Jobsite) => void;
}): JobsiteColumn[] =>
  ensureNonEmptyItems<JobsiteColumn>([
    {
      id: JobsiteColumnName.Jobsite,
      Header: 'Name',
      accessor: 'name',
      Cell: AvatarCell,
      CellLoading: (): React.ReactElement => (
        <AvatarSkeleton size="xs" type="rounded" hideDetails />
      ),
      componentProps: (jobsite: Jobsite): AvatarCellComponentProps => ({
        icon: BriefcaseIcon,
        type: 'rounded',
        placeholder: (jobsite.name.substring(0, 2) || '??').toUpperCase(),
        objectFit: 'cover',
        size: 'xs',
      }),
    },
    {
      id: JobsiteColumnName.Client,
      Header: 'Client',
      accessor: (jobsite: Jobsite): string => {
        return jobsite?.developer?.organization?.name;
      },
    },
    ...(isTemplate
      ? []
      : [
          {
            id: JobsiteColumnName.Location,
            Header: 'Location',
            accessor: (jobsite: Jobsite): string => {
              const { addressCity, addressState } = jobsite ?? {};
              return [addressCity, addressState].filter(Boolean).join(', ');
            },
          },
          {
            id: JobsiteColumnName.StartDate,
            Header: 'Start Date',
            accessor: (jobsite: Jobsite): string =>
              getPrettyFormattedUtcDate(jobsite?.startDate),
          },
          {
            id: JobsiteColumnName.EndDate,
            Header: 'End Date',
            accessor: (jobsite: Jobsite): string =>
              getPrettyFormattedUtcDate(jobsite?.endDate),
          },
        ]),
    {
      id: JobsiteColumnName.Actions,
      Header: '',
      Cell: DropdownButtonCell,
      CellLoading: (): React.ReactElement => (
        <DropdownButtonSkeleton cellAlignment="right" />
      ),
      componentProps: (jobsite: Jobsite): DropdownButtonCellComponentProps => {
        const { jobsiteId } = jobsite ?? {};
        return {
          cellAlignment: 'right',
          menuItems: ensureNonEmptyItems<MenuItemProps>([
            {
              icon: FileChartLineIcon,
              text: 'View',
              theme: 'secondary',
              onClick: (event): void =>
                navigateToJobsitePage(jobsite, shouldOpenInNewTab(event)),
            },
            user.isAllowed(to.editJobsiteConfiguration, jobsiteId) && {
              icon: CogIcon,
              text: 'Edit Configuration',
              theme: 'secondary',
              onClick: (event): void =>
                navigateToJobsiteConfigurationEditPage(
                  jobsite,
                  shouldOpenInNewTab(event),
                ),
            },
            !isTemplate &&
              user.isAllowed(to.closeOutJobsites, jobsiteId) && {
                icon: BusinessTimeIcon,
                text: 'Close out',
                theme: 'secondary',
                disabled: !!jobsite?.endDate,
                onClick: (): void => openCloseOutJobsiteModal(jobsite),
              },
            !isTemplate &&
              user.isAllowed(to.archiveJobsites) && {
                icon: TrashIcon,
                text: 'Archive',
                theme: 'danger',
                onClick: (): void => openArchiveJobsiteModal(jobsite),
              },
          ]),
        };
      },
    },
  ]);
